import { Injectable } from '@angular/core';
import { CuttingOptimizerService } from './cutting-optimizer.service';
import { LamelareaImpl } from "../../../../shared/product-card/product-card/lamelarea.class";
import { LamellaSet } from "../../../../shared/product-card/product-card/lamel/lamella-set.class";

@Injectable({
    providedIn: 'root'
})
export class QuantityCalculationService {

    private static readonly WORKING_RESERVE: number = 100;
    private static readonly BLADE_WIDTH: number = 10;
    private static readonly INCREASE: number = 100;

    private static readonly DELIVERY_MIN_LENGTH_ANODIZED: number = 5000;
    private static readonly DELIVERY_MIN_LENGTH_NON_ANODIZED: number = 6000;
    private static readonly DELIVERY_MAX_LENGTH_POWDER_COATED: number = 7900 - QuantityCalculationService.WORKING_RESERVE*2; //Duplicate this because of the 10mm blade width, dirty delimiter
    private static readonly DELIVERY_MAX_LENGTH_NON_POWDER_COATED: number = 6600 - QuantityCalculationService.WORKING_RESERVE*2; //Duplicate this because of the 10mm blade width, dirty delimiter

    private static readonly MOUNTING_DELIVERY_LENGTH: number = 6100;

    private static readonly GAP: number = 10;

    private static readonly MAX_LENGTH: number = 4000;
    private static readonly MIN_REMAINING_LENGTH: number = 1000;

    private static readonly INCREMENT: number = 26.5;
    private static readonly MOUNTING_MAX_LENGTH: number = Math.floor(2968 / QuantityCalculationService.INCREMENT) * QuantityCalculationService.INCREMENT;
    private static readonly MOUNTING_MIN_EXTENSION: number = Math.ceil(53 / QuantityCalculationService.INCREMENT) * QuantityCalculationService.INCREMENT;

    private static readonly FRAME_MAX_LENGTH: number = QuantityCalculationService.MOUNTING_MAX_LENGTH;
    private static readonly FRAME_MIN_EXTENSION: number = QuantityCalculationService.MOUNTING_MIN_EXTENSION;

    public debugLogs: string[] = [];
    public combinedLammellasWithRowCount: {
        key: string;
        productCode: string;
        rows: number;
        width: number;
        color: string;
    }[] = [];

    public allPieceList: {
        [key: string]: {
            productCode: string;
            color: string;
            material: string;
            pieces: { [key: number]: number }
        }
    } = {};

    public allFrameProfiles: {
        [key: string]: {
            productCode: string;
            color: string;
            material: string;
            pieces: { [key: number]: number }
        }
    } = {};

    public allMountingProfiles: {
        [key: string]: {
            productCode: string;
            color: string;
            material: string;
            pieces: { [key: number]: number }
        }
    } = {};

    public accessories = {
        fixplate1: 0,
        fixplate2: 0,
        fixplate4: 0,
        screws: 0
    };

    public lowestWasteCuttingPlan: any = [];
    public mountingProfilesCuttingPlan: any = [];
    public frameProfilesCuttingPlan: any = [];

    /**
     * Tyhjentää kaikki laskennan tuloksia.
     */
    private resetCalculationResults(): void {
        this.debugLogs = [];
        this.combinedLammellasWithRowCount = [];
        this.allPieceList = {};
        this.allFrameProfiles = {};
        this.allMountingProfiles = {};
        this.accessories = {
            fixplate1: 0,
            fixplate2: 0,
            fixplate4: 0,
            screws: 0
        };
        this.lowestWasteCuttingPlan = [];
        this.mountingProfilesCuttingPlan = [];
        this.frameProfilesCuttingPlan = [];
    }

    /**
     * Suorittaa laskennan aina tyhjentäen edelliset tulokset.
     */
    public recalculateAll(lamellaAreas: LamelareaImpl[], cuttingOptimizerService: CuttingOptimizerService): void {
        // Nollataan edelliset tulokset ennen uuden laskennan aloittamista
        this.resetCalculationResults();
        this.calculateLamella(lamellaAreas, cuttingOptimizerService);
        this.calculateMountingProfiles(lamellaAreas, cuttingOptimizerService);
        this.calculateFrameProfiles(lamellaAreas, cuttingOptimizerService)
    }

    /**
     * Yhdistää lamella-alueiden laskennan ja leikkaussuunnitelman generoinnin.
     */
    public calculateLamella(lamellaAreas: LamelareaImpl[], cuttingOptimizerService: CuttingOptimizerService): void {
        lamellaAreas.forEach(area => this.processLamellaArea(area));

        // Generoidaan leikkaussuunnitelmia eri toimituspituuksilla
        const cuttingPlans = [];

        const DELIVERY_MIN_LENGTH = this.isAllMaterialTypeAnodized(lamellaAreas)
            ? QuantityCalculationService.DELIVERY_MIN_LENGTH_ANODIZED
            : QuantityCalculationService.DELIVERY_MIN_LENGTH_NON_ANODIZED;
        const DELIVERY_MAX_LENGTH = this.isAllMaterialTypePowderCoated(lamellaAreas)
            ? QuantityCalculationService.DELIVERY_MAX_LENGTH_POWDER_COATED
            : QuantityCalculationService.DELIVERY_MAX_LENGTH_NON_POWDER_COATED;
        for (let deliveryTargetLength = DELIVERY_MIN_LENGTH; deliveryTargetLength <= DELIVERY_MAX_LENGTH - QuantityCalculationService.WORKING_RESERVE; deliveryTargetLength += QuantityCalculationService.INCREASE) {
            cuttingPlans.push(cuttingOptimizerService.generateCuttingPlan(this.allPieceList, deliveryTargetLength, 0));
        }
        // Valitaan suunnitelma, jossa on pienin hukka
        this.lowestWasteCuttingPlan = cuttingPlans.reduce((acc, plan) => plan.totalWaste < acc.totalWaste ? plan : acc);
        this.addWorkingReserveToCuttingPlan(this.lowestWasteCuttingPlan);
    }

    public calculateFrameProfiles(lamellaAreas: LamelareaImpl[], cuttingOptimizerService: CuttingOptimizerService): void {

        lamellaAreas.forEach(area => this.processFrameProfilesFromLamellaArea(area));

        const FIXED_DELIVERY_LENGTH = QuantityCalculationService.MOUNTING_DELIVERY_LENGTH - QuantityCalculationService.WORKING_RESERVE;

        // Generoidaan leikkaussuunnitelma ilman työvaraa, jonka jälkeen varmistetaan että jokaisesta profiilista löytyy työvara.
        this.frameProfilesCuttingPlan = this.addWorkingReserveToMountingProfileCuttingPlan(
            cuttingOptimizerService.generateCuttingPlan(this.allFrameProfiles, FIXED_DELIVERY_LENGTH, 0)
        );
    }

    public calculateMountingProfiles(lamellaAreas: LamelareaImpl[], cuttingOptimizerService: CuttingOptimizerService): void {
        lamellaAreas.forEach(area => this.processMountingProfilesFromLamellaArea(area));

        const FIXED_DELIVERY_LENGTH = QuantityCalculationService.MOUNTING_DELIVERY_LENGTH - QuantityCalculationService.WORKING_RESERVE;

        // Generoidaan leikkaussuunnitelma ilman työvaraa, jonka jälkeen varmistetaan että jokaisesta profiilista löytyy työvara.
        this.mountingProfilesCuttingPlan = this.addWorkingReserveToMountingProfileCuttingPlan(
            cuttingOptimizerService.generateCuttingPlan(this.allMountingProfiles, FIXED_DELIVERY_LENGTH, 0)
        );

    }

    private isAllMaterialTypeAnodized(lamellaAreas: LamelareaImpl[]): boolean {
        return lamellaAreas.every(area => area.lamellaSets.every(set => set.material === 'Anodized'));
    }

    private isAllMaterialTypePowderCoated(lamellaAreas: LamelareaImpl[]): boolean {
        return lamellaAreas.every(area => area.lamellaSets.every(set => set.material === 'Powder Coated'));
    }

    /** Päivitetty logiikka – vastaa insertLamellaSets-metodia */
    private processLamellaArea(lamellaArea: LamelareaImpl): void {
        const {
            lamellaSets,
            height: areaHeight,   // mm
            width: areaWidth,     // mm
            framePositions,
        } = lamellaArea;

        let usedHeight = 0;          // mm
        let lamellaSetIndex = 0;
        let previousSet: LamellaSet | null = null;
        let lastLamellaTop = 0;      // viimeisen säleen yläpinta (mm, ilman gap-väliä)

        while (usedHeight < areaHeight) {
            const current = { ...lamellaSets[lamellaSetIndex] };
            const productChanged =
                previousSet && previousSet.productCode !== current.productCode;

            /* gap ennen settiä jos tuote vaihtuu */
            if (productChanged) {
                usedHeight = this.addGapIfNeededMm(
                    usedHeight,
                    lastLamellaTop,
                    current.spaceAfter
                );
            }

            /* toistojen määrä – viimeinen lamella ilman välikköpakkoa */
            const unitHeight = current.lamellaSpace + current.lamellaSpaceBetween; // mm
            const free = areaHeight - usedHeight;
            const potential = Math.floor(
                (free + current.lamellaSpaceBetween) / unitHeight
            );
            current.lamellaRepeat =
                current.lamellaRepeat === Infinity
                    ? potential
                    : Math.min(current.lamellaRepeat, potential);

            if (current.lamellaRepeat <= 0) break;

            /* käsitellään jokainen lamella */
            for (let i = 0; i < current.lamellaRepeat && usedHeight < areaHeight; i++) {
                const key = `${current.productCode}_${current.color.RAL}_${current.material}`;

                this.updateCombinedLammellas(key, current, areaWidth);

                const cuts = this.optimalCuts(areaWidth, framePositions);
                const pieces = this.optimalCutsToPieceLengths(cuts);
                this.addRowRelatedAccessories(framePositions, cuts);
                this.updateAllPieceList(key, current, pieces);

                usedHeight += unitHeight;           // sisältää välin
            }

            /* viimeisen säleen yläpinta ilman väliä */
            lastLamellaTop = usedHeight - current.lamellaSpaceBetween;

            /* gap setin jälkeen jos tuote vaihtuu */
            if (productChanged) {
                usedHeight = this.addGapIfNeededMm(
                    usedHeight,
                    lastLamellaTop,
                    current.spaceBefore
                );
            }

            previousSet = current;
            lamellaSetIndex = (lamellaSetIndex + 1) % lamellaSets.length;
        }
    }

    /* Sama apumetodi kuin aiemmin */
    private addGapIfNeededMm(
        used: number,
        lastTop: number,
        requiredGap: number
    ): number {
        const existing = used - lastTop;
        return requiredGap > existing ? used + (requiredGap - existing) : used;
    }


    private processLamellaArea2(lamellaArea: LamelareaImpl): void {
        const { lamellaSets, height: areaHeight, width: areaWidth, framePositions } = lamellaArea;
        let processedHeight = 0;
        let lamellaSetIndex = 0;
        let lastLamellaSet: LamellaSet | null = null;
        const epsilon = 1e-6;

        let loop_1 = 0;

        while (processedHeight < areaHeight) {

            loop_1++;

            const lamellaSet = { ...lamellaSets[lamellaSetIndex] };

            // Jos tuotekoodeissa vaihtuu, lisätään spaceAfter
            if (lastLamellaSet && lamellaSet.productCode !== lastLamellaSet.productCode && lamellaSet.spaceAfter > 0) {
                this.debugLogs.push(`Product code changed: adding spaceAfter ${lamellaSet.spaceAfter}mm`);
                processedHeight += lamellaSet.spaceAfter;
            }

            lastLamellaSet = { ...lamellaSet };



            const unitHeight = lamellaSet.lamellaSpace + lamellaSet.lamellaSpaceBetween;
            const availableHeight = areaHeight - processedHeight;
            const computedRepeats = Math.floor((availableHeight + epsilon) / unitHeight);
            lamellaSet.lamellaRepeat = lamellaSet.lamellaRepeat === Infinity ? computedRepeats : Math.min(lamellaSet.lamellaRepeat, computedRepeats);

            if (lamellaSet.lamellaRepeat <= 0) {
                this.debugLogs.push(`No room for repeats lamellaSet ${lamellaSet.id}: setting processedHeight to ${areaHeight}`);
                processedHeight = areaHeight;
                break;
            }



            for (let i = 0; i < lamellaSet.lamellaRepeat && processedHeight < areaHeight; i++) {
                const key = `${lamellaSet.productCode}_${lamellaSet.color.RAL}_${lamellaSet.material}`;
                this.updateCombinedLammellas(key, lamellaSet, areaWidth);



                const optimalCutsForRow = this.optimalCuts(areaWidth, framePositions);
                const optimalPiecesForRow = this.optimalCutsToPieceLengths(optimalCutsForRow);
                this.addRowRelatedAccessories(framePositions, optimalCutsForRow);
                this.updateAllPieceList(key, lamellaSet, optimalPiecesForRow);

                processedHeight += unitHeight;

            }
            lamellaSetIndex = (lamellaSetIndex + 1) % lamellaSets.length;
        }
    }

    private processFrameProfilesFromLamellaArea(lamellaArea: LamelareaImpl): void {

        //Frame profiles are not always present
        if(!lamellaArea.frameProfile || !lamellaArea.frameProfile.productCode) {
            return;
        }

        const { height: areaHeight, frameProfile, framePositions } = lamellaArea;

        const key = `${frameProfile.productCode}_${frameProfile.color.RAL}_${frameProfile.materialType}`;

        const pieces: number[] = [];
        let remainingHeight = areaHeight;

        // Tämä laskee alueen täydellä pituudella, jätetään kunnes varmistuu että ei palauteta.
        /*if (areaHeight <= QuantityCalculationService.FRAME_MAX_LENGTH) {
            pieces.push(areaHeight);
        } else {
            const n = Math.floor(areaHeight / QuantityCalculationService.FRAME_MAX_LENGTH);
            const remainder = areaHeight - n * QuantityCalculationService.FRAME_MAX_LENGTH;

            if (remainder >= QuantityCalculationService.FRAME_MIN_EXTENSION) {
                for (let i = 0; i < n; i++) {
                    pieces.push(QuantityCalculationService.FRAME_MAX_LENGTH);
                }
                if (remainder > 0) {
                    pieces.push(remainder);
                }
            } else {
                const adjustedLength = areaHeight / n;
                for (let i = 0; i < n; i++) {
                    pieces.push(adjustedLength);
                }
            }
        }*/

        // Käytetään samaa laskentaa kuin kiinnityksissä
        while (remainingHeight > 0) {
            if (remainingHeight <= QuantityCalculationService.FRAME_MAX_LENGTH) {
                const piece = Math.floor(remainingHeight / QuantityCalculationService.INCREMENT) * QuantityCalculationService.INCREMENT;
                if (piece < QuantityCalculationService.FRAME_MIN_EXTENSION && pieces.length > 0) {
                    const lastPiece = pieces.pop()!;
                    const adjustedLastPiece = lastPiece - (QuantityCalculationService.FRAME_MIN_EXTENSION - piece);
                    const adjustedLastPieceRounded = Math.floor(adjustedLastPiece / QuantityCalculationService.INCREMENT) * QuantityCalculationService.INCREMENT;
                    pieces.push(adjustedLastPieceRounded);
                    pieces.push(areaHeight - pieces.reduce((acc, len) => acc + len, 0));
                } else {
                    pieces.push(piece);
                }
                break;
            } else {
                pieces.push(QuantityCalculationService.FRAME_MAX_LENGTH);
                remainingHeight -= QuantityCalculationService.FRAME_MAX_LENGTH;
            }
        }

        const multiplier = framePositions ? framePositions.length : 1;

        if (!this.allFrameProfiles) {
            this.allFrameProfiles = {};
        }
        if (!this.allFrameProfiles[key]) {
            this.allFrameProfiles[key] = {
                productCode: frameProfile.productCode,
                color: frameProfile.color.HEX,
                material: frameProfile.materialType,
                pieces: {}
            };
        }

        pieces.forEach((pieceLength: number) => {
            this.allFrameProfiles[key].pieces[pieceLength] =
                this.allFrameProfiles[key].pieces[pieceLength]
                    ? this.allFrameProfiles[key].pieces[pieceLength] + multiplier
                    : multiplier;
        });
    }

    private processMountingProfilesFromLamellaArea(lamellaArea: LamelareaImpl): void {
        const { height: areaHeight, mountingProfile, framePositions } = lamellaArea;

        const key = `${mountingProfile.productCode}_${mountingProfile.color.RAL}_${mountingProfile.materialType}`;

        const pieces: number[] = [];
        let remainingHeight = areaHeight;

        while (remainingHeight > 0) {
            if (remainingHeight <= QuantityCalculationService.MOUNTING_MAX_LENGTH) {
                const piece = Math.floor(remainingHeight / QuantityCalculationService.INCREMENT) * QuantityCalculationService.INCREMENT;
                if (piece < QuantityCalculationService.MOUNTING_MIN_EXTENSION && pieces.length > 0) {
                    const lastPiece = pieces.pop()!;
                    const adjustedLastPiece = lastPiece - (QuantityCalculationService.MOUNTING_MIN_EXTENSION - piece);
                    const adjustedLastPieceRounded = Math.floor(adjustedLastPiece / QuantityCalculationService.INCREMENT) * QuantityCalculationService.INCREMENT;
                    pieces.push(adjustedLastPieceRounded);
                    pieces.push(areaHeight - pieces.reduce((acc, len) => acc + len, 0));
                } else {
                    pieces.push(piece);
                }
                break;
            } else {
                pieces.push(QuantityCalculationService.MOUNTING_MAX_LENGTH);
                remainingHeight -= QuantityCalculationService.MOUNTING_MAX_LENGTH;
            }
        }

        const multiplier = framePositions ? framePositions.length : 1;

        if (!this.allMountingProfiles) {
            this.allMountingProfiles = {};
        }
        if (!this.allMountingProfiles[key]) {
            this.allMountingProfiles[key] = {
                productCode: mountingProfile.productCode,
                color: mountingProfile.color.HEX,
                material: mountingProfile.materialType,
                pieces: {}
            };
        }

        pieces.forEach((pieceLength: number) => {
            this.allMountingProfiles[key].pieces[pieceLength] =
                this.allMountingProfiles[key].pieces[pieceLength]
                    ? this.allMountingProfiles[key].pieces[pieceLength] + multiplier
                    : multiplier;
        });

    }

    private updateCombinedLammellas(key: string, lamellaSet: LamellaSet, areaWidth: number): void {
        const entry = this.combinedLammellasWithRowCount.find(p => p.key === key);
        if (entry) {
            entry.rows++;
        } else {
            this.combinedLammellasWithRowCount.push({
                key,
                productCode: lamellaSet.productCode,
                rows: 1,
                width: areaWidth,
                color: lamellaSet.color.HEX
            });
        }
    }

    private updateAllPieceList(key: string, lamellaSet: LamellaSet, optimalPiecesForRow: number[]): void {
        if (this.allPieceList[key] && Object.keys(this.allPieceList[key].pieces).length > 0) {
            optimalPiecesForRow.forEach(optimalCut => {
                this.allPieceList[key].pieces[optimalCut] = (this.allPieceList[key].pieces[optimalCut] || 0) + 1;
            });
        } else {
            this.allPieceList[key] = {
                productCode: lamellaSet.productCode,
                color: lamellaSet.color.HEX,
                material: lamellaSet.material,
                pieces: {}
            };
            optimalPiecesForRow.forEach(optimalCut => {
                this.allPieceList[key].pieces[optimalCut] = 1;
            });
        }
    }

    /**
     * Muuntaa optimaaliset leikkauspaikat pätkien pituuksiksi:
     * - Ensimmäisestä ja viimeisestä pätkästä vähennetään GAP/2
     * - Muista pätkistä vähennetään täysi GAP
     */
    private optimalCutsToPieceLengths(optimalCuts: number[]): number[] {
        const pieceLengths: number[] = [];
        let lastCut = 0;
        for (const cut of optimalCuts) {
            pieceLengths.push(cut - lastCut);
            lastCut = cut;
        }

        if (pieceLengths.length > 1) {
            for (let i = 0; i < pieceLengths.length; i++) {
                pieceLengths[i] -= (i === 0 || i === pieceLengths.length - 1)
                    ? QuantityCalculationService.GAP / 2
                    : QuantityCalculationService.GAP;
            }
        }
        return pieceLengths;
    }

    /**
     * Laskee optimaaliset leikkauspaikat lamellalle siten, että:
     * - Leikataan korkeintaan MAX_LENGTH pituinen pala
     * - Lopun tulee olla vähintään MIN_REMAINING_LENGTH
     */
    private optimalCuts(lamellaTargetLength: number, framePositions: { position: number }[]): number[] {
        const cuts: number[] = [];

        if (lamellaTargetLength <= QuantityCalculationService.MAX_LENGTH) {
            cuts.push(lamellaTargetLength);
            return cuts;
        }

        let lastCutPosition = 0;
        while (lastCutPosition + QuantityCalculationService.MAX_LENGTH < lamellaTargetLength) {
            const maxAllowedPosition = lastCutPosition + QuantityCalculationService.MAX_LENGTH;
            const candidates = framePositions.filter(frame => frame.position > lastCutPosition && frame.position <= maxAllowedPosition);
            let bestFramePosition: number | null = candidates.length > 0 ? Math.max(...candidates.map(f => f.position)) : null;
            if (bestFramePosition !== null) {
                if (lamellaTargetLength - bestFramePosition < QuantityCalculationService.MIN_REMAINING_LENGTH) break;
                cuts.push(bestFramePosition);
                lastCutPosition = bestFramePosition;
            } else {
                if (lamellaTargetLength - maxAllowedPosition < QuantityCalculationService.MIN_REMAINING_LENGTH) break;
                cuts.push(maxAllowedPosition);
                lastCutPosition = maxAllowedPosition;
            }
        }
        if (cuts.length === 0 || cuts[cuts.length - 1] !== lamellaTargetLength) {
            cuts.push(lamellaTargetLength);
        }
        return cuts;
    }

    private addRowRelatedAccessories(framePositions: { position: number }[], optimalCuts: number[]): void {
        const cutCount = optimalCuts.length;
        if (cutCount === 0) return;
        this.accessories.fixplate1 += cutCount;
        if (cutCount > 1) {
            this.accessories.fixplate4 += cutCount - 1;
        }
        const fixplate2Count = Math.max(framePositions.length - cutCount, 0);
        this.accessories.fixplate2 += fixplate2Count;
        this.accessories.screws = this.accessories.fixplate1 + this.accessories.fixplate2 + this.accessories.fixplate4 * 2;
    }

    /**
     * Varmistaa, että leikkaussuunnitelmassa on vähintään 100 mm työvara.
     */
    private addWorkingReserveToCuttingPlan(cuttingPlan: any): any {



        const profiles = cuttingPlan.profiles;

        // Etsitään pienimmän hukan omaava profiili
        const minWasteProfile = profiles.reduce((prev: any, curr: any) => {
            return prev.leftover < curr.leftover ? prev : curr;
        });

        if (minWasteProfile.leftover >= QuantityCalculationService.WORKING_RESERVE + (minWasteProfile.pieces.length * QuantityCalculationService.BLADE_WIDTH)) {
            return cuttingPlan;
        }

        // Jokainen leikkausprofiilissa pitää pidentää käytettävää profiilia 10mm(QuantityCalculationService.BLADE_WIDTH)
        const totalWorkingReserve = QuantityCalculationService.WORKING_RESERVE + (minWasteProfile.pieces.length * QuantityCalculationService.BLADE_WIDTH);
        let newLength = minWasteProfile.pieces.reduce((sum: number, piece: any) => sum + piece.length, 0) + totalWorkingReserve;

        // Always ceil full profiles to next 10mm (Laurin's request)
        newLength = Math.ceil(newLength / 10) * 10;

        for (const profile of profiles) {
            const totalPieceLength = profile.pieces.reduce((sum: number, p: any) => sum + p.length, 0);
            profile.leftover = newLength - totalPieceLength;
        }

        cuttingPlan.profileLength = newLength;
        cuttingPlan.totalWaste = profiles.reduce((sum: number, p: any) => sum + p.leftover, 0);
        cuttingPlan.profiles = profiles;

        return cuttingPlan;
    }

    private addWorkingReserveToMountingProfileCuttingPlan(cuttingPlan: any): any {
        const profiles = cuttingPlan.profiles;
        const newLength = cuttingPlan.profileLength + QuantityCalculationService.WORKING_RESERVE;

        for (const profile of profiles) {
            const totalPieceLength = profile.pieces.reduce((sum: number, p: any) => sum + p.length, 0);
            profile.leftover = newLength - totalPieceLength;
        }

        cuttingPlan.profileLength = newLength;
        cuttingPlan.totalWaste = profiles.reduce((sum: number, p: any) => sum + p.leftover, 0);
        cuttingPlan.profiles = profiles;

        return cuttingPlan;
    }
}
